/* eslint-disable camelcase */

import { EventEmitter } from 'events';
import { computeFilletCandidatePositions, FilletTypes } from './fillet_utils';

const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

/**
 * パーツのフィレット（角の丸み）を管理するクラス
 * SVGを使用して視覚的な編集インターフェースを提供する
 */
export default class Part extends EventEmitter {
  /**
   * @param {Object} path - パスデータ（外周、内周、ポケットを含む）
   * @param {number} mill - フィレットの直径（デフォルト6.35mm）
   */
  constructor (path, mill = 6.35) {
    super();

    this.mill = mill; // フィレットの直径

    // SVG要素の初期設定
    this.position = new THREE.Vector2();
    this.polylines = [];
    this.element = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    // 元のパスデータを保存
    this.pathOrigin = path;
    const { ex_path, in_paths, pocket } = path;

    // Y座標の最大値を計算（座標系の変換に使用）
    let maxY = Number.MIN_SAFE_INTEGER;
    ex_path.map(p => {
      maxY = Math.max(maxY, p.y);
    });
    this.maxY = maxY;

    // パスをVector2オブジェクトに変換
    // 外周パス
    this.exPathOrigin = this.toVector(ex_path, this.maxY);
    // 内周パス（複数可）
    this.inPathsOrigin = in_paths.map(path => {
      return this.toVector(path, this.maxY);
    });
    // ポケットパス（外周と内周を持つ）
    this.poPathsOrigin = pocket.map(po => {
      const { ex_pocket, in_pocket } = po;
      return {
        ex_pocket: this.toVector(ex_pocket, this.maxY),
        in_pocket: in_pocket.map(path => {
          return this.toVector(path, this.maxY);
        })
      };
    });

    // フィレット適用前の初期状態をコピー
    this.exFilleted = [ this.exPathOrigin.slice() ];  // 外周
    this.inFilleted = this.inPathsOrigin.map(p => [p.slice()]);  // 内周
    this.poFilleted = this.poPathsOrigin.map(po => {  // ポケット
      const { ex_pocket, in_pocket } = po;
      return {
        ex_pocket: [ ex_pocket.slice() ],
        in_pocket: in_pocket.map(p => {
          return [ p.slice() ];
        })
      };
    });

    // フィレット候補位置の計算と視覚化

    // 1. 外周のフィレット候補
    this.exCandidates = computeFilletCandidatePositions({
      points: this.exPathOrigin,
      isExterior: true  // 外周として処理
    });
    // 外周の各候補位置に赤い丸を表示
    this.exCandidates.forEach(point => {
      this.createCircleElement(point);
    });

    // 2. 内周のフィレット候補（複数の内周を処理）
    this.inCandidates = this.inPathsOrigin.map(path => {
      return computeFilletCandidatePositions({
        points: path,
        isExterior: false  // 内周として処理
      });
    });
    // 内周の各候補位置に赤い丸を表示
    this.inCandidates.forEach(candidates => {
      candidates.forEach(point => {
        this.createCircleElement(point);
      });
    });

    // 3. ポケットのフィレット候補
    this.poCandidates = this.poPathsOrigin.map(po => {
      const { ex_pocket, in_pocket } = po;
      return {
        // ポケットの外周
        ex_pocket: computeFilletCandidatePositions({
          points: ex_pocket,
          isExterior: true,   // 外周として処理
          isPocket: true,     // ポケットとして処理
          outline: this.exPathOrigin  // 外形線の情報を追加
        }),
        // ポケットの内周
        in_pocket: in_pocket.map(p => {
          return computeFilletCandidatePositions({
            points: p,
            isExterior: false,  // 内周として処理
            isPocket: true,    // ポケットとして処理
            outline: this.exPathOrigin  // 外形線の情報を追加
          });
        })
      };
    });
    // ポケットの各候補位置に赤い丸を表示
    this.poCandidates.forEach(c => {
      const { ex_pocket, in_pocket } = c;
      // ポケット外周の候補を表示
      ex_pocket.forEach(p => {
        this.createCircleElement(p);
      });
      // ポケット内周の候補を表示
      in_pocket.forEach(ps => {
        ps.forEach(p => {
          this.createCircleElement(p);
        });
      });
    });

    this.update();
  }

  getAllCandidates () {
    const { exCandidates, inCandidates, poCandidates } = this;
    const po = poCandidates.map(po => {
      const { ex_pocket, in_pocket } = po;
      return in_pocket.flat().concat(ex_pocket);
    }).flat();
    return inCandidates.flat().concat(exCandidates).concat(po);
  }

  moveTo (x, y) {
    this.position.x = x;
    this.position.y = y;
    this.transform();
  }

  transform () {
    this.element.style.transform = `translate(${this.position.x}px, ${this.position.y}px)`;
  }

  select (point, type = FilletTypes.DogBone) {
    point.type = type;
    this.apply();
  }

  force (point, type) {
    point.type = type;
    this.apply();
  }

  applyExPathFillet (origin, candidates, isPocket = false) {
    const exPaths = [origin];
    const exFillets = candidates.filter(cand => cand.type !== FilletTypes.None).map(cand => {
      return this.computeFilletPosition(cand.a, cand.o, cand.b, cand.index, cand.type, this.mill);
    });
    exFillets.forEach(p => {
      exPaths.push(this.getCirclePoints(p, this.mill * 0.5));
    });
    if (isPocket) {
      return this.union(exPaths);
    }
    return this.difference(exPaths);
  }

  applyInPathFillet (origin, candidates) {
    const inPaths = [ origin ];
    const inFillets = candidates.filter(cand => cand.type !== FilletTypes.None).map(cand => {
      return this.computeFilletPosition(cand.a, cand.o, cand.b, cand.index, cand.type, this.mill);
    });
    inFillets.forEach(p => {
      inPaths.push(this.getCirclePoints(p, this.mill * 0.5));
    });
    return this.union(inPaths);
  }

  apply () {
    this.exFilleted = this.applyExPathFillet(this.exPathOrigin, this.exCandidates);
    this.inFilleted = this.inPathsOrigin.map((inPathOrigin, idx) => {
      return this.applyInPathFillet(inPathOrigin, this.inCandidates[idx]);
    });
    this.poFilleted = this.poPathsOrigin.map((po, idx) => {
      const { ex_pocket, in_pocket } = po;
      const candidates = this.poCandidates[idx];
      return {
        ex_pocket: this.applyExPathFillet(ex_pocket, candidates.ex_pocket, true),
        in_pocket: in_pocket.map((path, j) => {
          return this.applyInPathFillet(path, candidates.in_pocket[j]);
        })
      };
    });

    this.update();
  }

  build () {
    const json = JSON.parse(JSON.stringify(this.pathOrigin)); // clone
    json.ex_path = this.toXY(this.exFilleted[0], this.maxY);
    json.in_paths = this.inFilleted.map(fillet => {
      return this.toXY(fillet[0], this.maxY);
    });
    json.pocket = this.poFilleted.map(po => {
      const { ex_pocket, in_pocket } = po;
      return {
        ex_pocket: this.toXY(ex_pocket[0], this.maxY),
        in_pocket: in_pocket.map(fillet => {
          return this.toXY(fillet[0], this.maxY);
        })
      };
    });
    return json;
  }

  serialize () {
    return {
      ex: this.exCandidates.map(p => p.type),
      in: this.inCandidates.map(points => points.map(p => p.type)),
      pocket: this.poCandidates.map(po => {
        const { ex_pocket, in_pocket } = po;
        return {
          ex_pocket: ex_pocket.map(p => p.type),
          in_pocket: in_pocket.map(points => {
            return points.map(p => p.type);
          })
        };
      })
    };
  }

  deserialize (source = { ex: [], in: [] }) {
    const { ex: exC, in: inC, pocket } = source;
    if (exC.length !== this.exCandidates.length || inC.length !== this.inCandidates.length || pocket.length !== this.poCandidates.length) return;

    exC.forEach((type, idx) => {
      this.exCandidates[idx].type = type;
    });
    inC.forEach((c, i) => {
      const points = this.inCandidates[i];
      if (points.length === c.length) {
        c.forEach((type, j) => {
          points[j].type = type;
        });
      }
    });
    pocket.forEach((po, i) => {
      const poc = this.poCandidates[i];
      const { ex_pocket, in_pocket } = poc;
      if (ex_pocket.length === po.ex_pocket.length) {
        po.ex_pocket.forEach((type, j) => {
          ex_pocket[j].type = type;
        });
      }
      if (in_pocket.length === po.in_pocket.length) {
        po.in_pocket.forEach((points, j) => {
          points.forEach((type, k) => {
            in_pocket[j][k].type = type;
          });
        });
      }
    });

    this.apply();
  }

  update () {
    this.polylines.forEach(poly => {
      this.element.removeChild(poly);
      poly.remove();
    });
    const polylines = this.exFilleted.map(points => {
      const poly = this.createPolylineElement(points);
      this.element.appendChild(poly);
      return poly;
    });
    this.inFilleted.forEach(result => {
      result.forEach(points => {
        const poly = this.createPolylineElement(points);
        this.element.appendChild(poly);
        polylines.push(poly);
      });
    });
    this.poFilleted.forEach(po => {
      const { ex_pocket, in_pocket } = po;
      ex_pocket.forEach(points => {
        const poly = this.createPolylineElement(points);
        this.element.appendChild(poly);
        polylines.push(poly);
      });
      in_pocket.forEach((po) => {
        po.forEach(points => {
          const poly = this.createPolylineElement(points);
          this.element.appendChild(poly);
          polylines.push(poly);
        });
      });
    });
    this.polylines = polylines;
  }

  bounds () {
    const points = this.exPathOrigin;

    let minX = 0;
    let minY = 0;
    let maxX = 0;
    let maxY = 0;

    if (points.length > 0) {
      let head = points[0];
      minX = head.x;
      maxX = head.x;
      minY = head.y;
      maxY = head.y;
    }

    points.forEach(p => {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    });
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  createPolylineElement (points = []) {
    const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    polyline.setAttribute('stroke', 'black');
    polyline.setAttribute('fill-opacity', 0);
    polyline.style.pointerEvents = 'none';

    points.forEach(p => {
      const point = svg.createSVGPoint();
      point.x = p.x;
      point.y = p.y;
      polyline.points.appendItem(point);
    });

    if (points.length > 0) {
      const point = svg.createSVGPoint();
      const p = points[0];
      point.x = p.x;
      point.y = p.y;
      polyline.points.appendItem(point);
    }

    return polyline;
  }

  createCircleElement (point) {
    const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    c.setAttribute('cx', point.o.x);
    c.setAttribute('cy', point.o.y);
    c.setAttribute('r', 10);
    // c.setAttribute('fill', '#333');
    c.setAttribute('fill', '#ff0000');
    c.setAttribute('fill-opacity', 0.1);
    c.style.cursor = 'crosshair';
    this.element.appendChild(c);
    c.addEventListener('mousedown', (e) => {
      this.emit('select', this, point);
      // emit select to update the point.type. Without emiting "select", point.type will always be -1(FilletTypes.None)
      if(point.type !== FilletTypes.None){
        c.setAttribute('fill', '#333');
      } else{
        c.setAttribute('fill', '#ff0000');
      }
    });
    point.on('select', () => {
      if (point.selected) {
        c.classList.add('selected');
        c.dispatchEvent(new Event("mousedown"));
      } else {
        c.classList.remove('selected');
      }
    });
    return c;
  }

  difference (paths) {
    const clip = new ClipperLib.Clipper();
    const precision = 1e8;
    paths.forEach((path, i) => {
      const ipath = path.map(p => {
        return new ClipperLib.IntPoint(p.x * precision, p.y * precision);
      });
      if (i === 0) {
        clip.AddPaths([ipath], ClipperLib.PolyType.ptSubject, true);
      } else {
        clip.AddPaths([ipath], ClipperLib.PolyType.ptClip, true);
      }
    });
    const solution = new ClipperLib.Paths();
    clip.Execute(ClipperLib.ClipType.ctDifference, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
    return solution.map(ipath => {
      return ipath.map(ip => {
        return new THREE.Vector2(ip.X, ip.Y).divideScalar(precision);
      });
    });
  }

  union (paths) {
    const clip = new ClipperLib.Clipper();
    const precision = 1e8;
    paths.forEach((path, i) => {
      const ipath = path.map(p => {
        return new ClipperLib.IntPoint(p.x * precision, p.y * precision);
      });
      if (i === 0) {
        clip.AddPaths([ipath], ClipperLib.PolyType.ptSubject, true);
      } else {
        clip.AddPaths([ipath], ClipperLib.PolyType.ptClip, true);
      }
    });
    const solution = new ClipperLib.Paths();
    clip.Execute(ClipperLib.ClipType.ctUnion, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
    return solution.map(ipath => {
      return ipath.map(ip => {
        return new THREE.Vector2(ip.X, ip.Y).divideScalar(precision);
      });
    });
  }

  getCirclePoints (center, radius, resolution = 32) {
    const path = [];
    const inv = 1 / resolution;
    for (let i = 0.001; i < resolution; i++) {
      const angle = i * inv * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      path.push(center.clone().add(new THREE.Vector2(cos * radius, sin * radius)));
    }
    return path;
  }

  /**
   * フィレットの具体的な位置を計算する
   * @param {Vector2} a - 前の点
   * @param {Vector2} o - 現在の点（角の頂点）
   * @param {Vector2} b - 次の点
   * @param {number} index - 点のインデックス
   * @param {FilletTypes} filletType - フィレットの種類
   * @param {number} mill - フィレットの直径
   * @returns {Vector2} フィレットの中心位置
   */
  computeFilletPosition (a, o, b, index, filletType, mill) {
    // 前の点から現在の点へのベクトル（正規化）
    const va = a.clone().sub(o).normalize();
    // 次の点から現在の点へのベクトル（正規化）
    const vb = b.clone().sub(o).normalize();

    switch (filletType) {
      case FilletTypes.Clockwise:
        // 時計回り：次の点方向にmill/2だけオフセット
        return o.clone().add(vb.multiplyScalar(mill * 0.5));
      case FilletTypes.CounterClockwise:
        // 反時計回り：前の点方向にmill/2だけオフセット
        return o.clone().add(va.multiplyScalar(mill * 0.5));
      case FilletTypes.I: {
        // I型：偶数インデックスは時計回り、奇数は反時計回り
        if (index % 2 === 0) {
          return this.computeFilletPosition(a, o, b, index, FilletTypes.Clockwise, mill);
        } else {
          return this.computeFilletPosition(a, o, b, index, FilletTypes.CounterClockwise, mill);
        }
      }
      case FilletTypes.H: {
        // H型：奇数インデックスは時計回り、偶数は反時計回り（I型の逆）
        if (index % 2 !== 0) {
          return this.computeFilletPosition(a, o, b, index, FilletTypes.Clockwise, mill);
        } else {
          return this.computeFilletPosition(a, o, b, index, FilletTypes.CounterClockwise, mill);
        }
      }
      case FilletTypes.DogBone:
      default:
        // 犬骨型：二等分線方向にmill/2だけオフセット
        return o.clone().add(va.add(vb).normalize().multiplyScalar(mill * 0.5));
    }
  }

  toVector (path, maxY) {
    if (!path) return [];
    return path.map(v => {
      return new THREE.Vector2().set(v.x, maxY - v.y);
    });
  }

  toXY (path, maxY) {
    if (!path) return [];
    return path.map(v => {
      return {
        x: v.x, y: maxY - v.y
      };
    });
  }

  updateMill (mill) {
    this.mill = mill;
    this.apply();
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/fillet/part.js